import axios from 'axios';
import { MAPBOX_ACCESS_TOKEN, MAPBOX_DIRECTIONS_URL } from '../config/config';
import { ROUTE_PARAMS, toLonLatPath, toRouteResult } from '../Utils/routeShape';

// Mapbox Search Box API (v1) - better POI coverage than Geocoding API
const MAPBOX_SEARCH_URL = 'https://api.mapbox.com/search/searchbox/v1/suggest';
const MAPBOX_RETRIEVE_URL = 'https://api.mapbox.com/search/searchbox/v1/retrieve';

// Simple session token for Mapbox Search API (reuse for billing optimization)
let sessionToken = null;
function getSessionToken() {
  if (!sessionToken) {
    sessionToken = crypto.randomUUID();
  }
  return sessionToken;
}

/**
 * Fetch address suggestions using Mapbox Search Box API
 * Better support for POIs (airports, hotels) and full addresses with house numbers
 * @param {string} query - Search query
 * @param {number} limit - Max number of results (default 5)
 * @returns {Promise<Array<{displayName: string, lat: string, lon: string}>>}
 */
export async function fetchAddressSuggestions(query, limit = 10) {
  if (!query || query.length < 3) {
    return [];
  }

  try {
    // Step 1: Get suggestions
    const suggestResponse = await axios.get(MAPBOX_SEARCH_URL, {
      params: {
        q: query,
        access_token: MAPBOX_ACCESS_TOKEN,
        limit,
        session_token: getSessionToken(),
        proximity: '19.5033,47.1625', // Hungary center coordinates (lon,lat)
        language: 'en',
        types: 'poi,address,place,street',
      },
    });

    if (!suggestResponse.data?.suggestions?.length) {
      return [];
    }

    // Step 2: Retrieve full details for each suggestion to get coordinates
    const results = await Promise.all(
      suggestResponse.data.suggestions.map(async (suggestion) => {
        try {
          const retrieveResponse = await axios.get(`${MAPBOX_RETRIEVE_URL}/${suggestion.mapbox_id}`, {
            params: {
              access_token: MAPBOX_ACCESS_TOKEN,
              session_token: getSessionToken(),
            },
          });

          const feature = retrieveResponse.data?.features?.[0];
          if (feature?.geometry?.coordinates) {
            // Build display name with house number in European format
            const props = feature.properties || {};
            const ctx = props.context || {};
            const parts = [];

            // Get context values
            const postcode = ctx.postcode?.name || props.postcode;
            const place = ctx.place?.name || props.place;
            const country = ctx.country?.name || props.country;

            // Check if this is a POI (has poi_category or feature_type is 'poi')
            const isPoi = props.poi_category || props.feature_type === 'poi';

            // For POIs, try to get a clean name without embedded address
            let mainName = '';
            if (isPoi) {
              // For POIs, use just the name without any address suffix
              // Mapbox sometimes embeds "Name 1234 City" - we want just "Name"
              const rawName = props.name_preferred || props.name || '';
              // Strip trailing patterns like "1087 Budapest" or "1300 Schwechat"
              // Also handle cases with multiple words like "1087 Budapest City"
              mainName = rawName.replace(/\s+\d{4,5}\s+[A-Za-zÀ-ÿ\s]+$/, '').trim();
            } else {
              // For addresses, use full name
              mainName = props.name_preferred || props.name || '';
              // Append house number if not already in name
              if (mainName && props.address && !mainName.includes(props.address)) {
                mainName = `${mainName} ${props.address}`;
              }
            }

            if (mainName) {
              parts.push(mainName);
            }

            // For POIs, add the street address after the name
            // Try to get street from context, then combine with house number
            // Skip if it's just a postcode+city pattern (no actual street)
            const street = ctx.street?.name || ctx.address?.street || props.street;
            const houseNumber = ctx.address?.address_number || props.address_number;
            if (isPoi && street && !/^\d{4,5}\s+[A-Za-zÀ-ÿ]+$/.test(street)) {
              // European format: "Street Name 123"
              const fullStreet = houseNumber ? `${street} ${houseNumber}` : street;
              parts.push(fullStreet);
            }

            // Add postcode if available
            if (postcode) parts.push(postcode);

            // Add city/place - skip if same as main name
            if (place && place.toLowerCase() !== mainName.toLowerCase()) {
              parts.push(place);
            }

            // Add country (skip region for cleaner display)
            if (country) parts.push(country);

            // Use constructed name or fallback
            const displayName = parts.length > 0
              ? parts.join(', ')
              : (suggestion.full_address || suggestion.name);

            return {
              displayName,
              lat: feature.geometry.coordinates[1].toString(),
              lon: feature.geometry.coordinates[0].toString(),
            };
          }
        } catch (err) {
          // Skip this suggestion if retrieve fails
        }
        return null;
      }),
    );

    return results.filter(Boolean);
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Error fetching address suggestions from Mapbox:', error);
  }
  return [];
}

/**
 * Get route between two coordinates using Mapbox Directions API
 * @param {Object} from - Origin coordinates {lat, lon}
 * @param {Object} to - Destination coordinates {lat, lon}
 * @param {Array} waypoints - Optional intermediate waypoints [{lat, lon}]
 * @returns {Promise<{distance: number, duration: number, geometry: Object}|null>}
 */
export async function getRoute(from, to, waypoints = []) {
  if (!from?.lat || !from?.lon || !to?.lat || !to?.lon) {
    return null;
  }

  try {
    // from -> waypoints (the usable ones, in order) -> to
    const points = [from, ...waypoints.filter((wp) => wp?.lat && wp?.lon), to];

    const response = await axios.get(`${MAPBOX_DIRECTIONS_URL}/${toLonLatPath(points)}`, {
      params: { access_token: MAPBOX_ACCESS_TOKEN, ...ROUTE_PARAMS },
    });

    if (response.data?.routes?.length > 0) {
      return toRouteResult(response.data.routes[0]);
    }
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Error fetching route from Mapbox:', error);
  }

  return null;
}

/**
 * Get the Mapbox access token
 * @returns {string}
 */
export function getMapboxToken() {
  return MAPBOX_ACCESS_TOKEN;
}

/**
 * Generate a static map image URL using Mapbox Static Images API
 * Great for print-friendly maps that don't require JavaScript
 * @param {Object} from - Origin coordinates {lat, lon}
 * @param {Object} to - Destination coordinates {lat, lon}
 * @param {Array} stops - Optional intermediate stops [{latitude, longitude, stop_type}]
 * @param {Object} options - Optional settings
 * @param {number} options.width - Image width (default 600, max 1280)
 * @param {number} options.height - Image height (default 400, max 1280)
 * @param {string} options.style - Map style (default 'streets-v12')
 * @param {boolean} options.retina - Use @2x retina (default true for print)
 * @returns {Promise<{url: string, distance: number, duration: number}|null>} - URL and route info
 */
export async function getStaticMapUrl(from, to, stops = [], options = {}) {
  if (!from?.lat || !from?.lon || !to?.lat || !to?.lon) {
    return null;
  }

  const {
    width = 600,
    height = 400,
    style = 'streets-v12',
    retina = true,
  } = options;

  // Collect all points for bounding box calculation
  const allPoints = [
    { lon: parseFloat(from.lon), lat: parseFloat(from.lat) },
    { lon: parseFloat(to.lon), lat: parseFloat(to.lat) },
  ];

  // Build markers array
  // Format: pin-{size}-{label}+{color}({lon},{lat})
  const markers = [];

  // Intermediate stops (orange/blue based on type)
  // Labels: A = pickup, B/C/D... for stops, last letter = dropoff
  const validStops = stops.filter((s) => s.latitude && s.longitude);
  const alphabet = 'abcdefghijklmnopqrstuvwxyz';

  // Pickup marker (green, A)
  markers.push(`pin-l-a+28a745(${from.lon},${from.lat})`);

  // Intermediate stops get B, C, D, etc.
  validStops.forEach((stop, index) => {
    const color = stop.stop_type === 'activity' ? '0d6efd' : 'eba23a';
    const label = alphabet[index + 1]; // B, C, D, etc.
    markers.push(`pin-s-${label}+${color}(${stop.longitude},${stop.latitude})`);
    allPoints.push({ lon: parseFloat(stop.longitude), lat: parseFloat(stop.latitude) });
  });

  // Dropoff marker (red) - next letter after stops
  const dropoffLabel = alphabet[validStops.length + 1]; // If 0 stops: B, if 1 stop: C, etc.
  markers.push(`pin-l-${dropoffLabel}+dc3545(${to.lon},${to.lat})`);

  // Get route for distance/duration info and path
  const route = await getRoute(
    from,
    to,
    validStops.map((s) => ({ lat: s.latitude, lon: s.longitude })),
  );

  // Build overlays string with markers only
  // Note: Path overlay removed - Mapbox polyline encoding adds complexity and URL length issues
  const overlays = markers.join(',');

  // Calculate bounding box
  const lons = allPoints.map((p) => p.lon);
  const lats = allPoints.map((p) => p.lat);
  const minLon = Math.min(...lons);
  const maxLon = Math.max(...lons);
  const minLat = Math.min(...lats);
  const maxLat = Math.max(...lats);

  // Calculate center
  const centerLon = (minLon + maxLon) / 2;
  const centerLat = (minLat + maxLat) / 2;

  // Calculate zoom level based on bounding box size
  const latDiff = maxLat - minLat;
  const lonDiff = maxLon - minLon;
  const maxDiff = Math.max(latDiff, lonDiff);

  let zoom;
  if (maxDiff > 10) zoom = 3;
  else if (maxDiff > 5) zoom = 4;
  else if (maxDiff > 2) zoom = 5;
  else if (maxDiff > 1) zoom = 6;
  else if (maxDiff > 0.5) zoom = 7;
  else if (maxDiff > 0.2) zoom = 8;
  else if (maxDiff > 0.1) zoom = 9;
  else if (maxDiff > 0.05) zoom = 10;
  else if (maxDiff > 0.02) zoom = 11;
  else if (maxDiff > 0.01) zoom = 12;
  else zoom = 13;

  const retinaStr = retina ? '@2x' : '';
  // URL encode the overlays to handle special characters properly
  const encodedOverlays = encodeURIComponent(overlays);
  const url = `https://api.mapbox.com/styles/v1/mapbox/${style}/static/${encodedOverlays}/${centerLon.toFixed(6)},${centerLat.toFixed(6)},${zoom}/${width}x${height}${retinaStr}?access_token=${MAPBOX_ACCESS_TOKEN}`;

  return {
    url,
    distance: route?.distance || null,
    duration: route?.duration || null,
  };
}

export default {
  getRoute,
  getMapboxToken,
  getStaticMapUrl,
  fetchAddressSuggestions,
};
